import React from 'react';
import { Link } from 'react-router-dom';
import { ENV_CONFIG } from '../constants';
import {
  ShieldCheckIcon,
  UserGroupIcon,
  AcademicCapIcon,
  BuildingOfficeIcon,
  GlobeAltIcon,
  DocumentTextIcon,
  TrophyIcon,
  LightBulbIcon,
  ArrowRightIcon,
  CalendarIcon,
  UsersIcon,
  PresentationChartLineIcon,
  BriefcaseIcon,
  SparklesIcon,
} from '@heroicons/react/24/outline';

const HomePage: React.FC = () => {
  const currentYear = new Date().getFullYear();
  const yearsActive = currentYear - 2014;

  const statistics = [
    {
      number: `${yearsActive}+`,
      label: 'Years of Excellence',
      icon: CalendarIcon,
    },
    {
      number: '12+',
      label: 'National Conferences',
      icon: PresentationChartLineIcon,
    },
    {
      number: 'All',
      label: 'Geopolitical Zones',
      icon: GlobeAltIcon,
    },
    {
      number: '2025',
      label: 'Research Excellence Award',
      icon: TrophyIcon,
    },
  ];

  const objectives = [
    {
      icon: LightBulbIcon,
      title: 'Knowledge Expansion',
      description: 'Host healthy debates to expand understanding of cybersecurity issues',
    },
    {
      icon: CalendarIcon,
      title: 'Industry Growth',
      description: 'Organize conferences, workshops, and seminars to grow Nigeria\'s information security industry',
    },
    {
      icon: AcademicCapIcon,
      title: 'Public Education',
      description: 'Educate organizations and the public on cyber threats and safeguards',
    },
    {
      icon: UsersIcon,
      title: 'Awareness Campaigns',
      description: 'Raise awareness among individuals, communities, schools, businesses, and government',
    },
    {
      icon: BriefcaseIcon,
      title: 'Professional Development',
      description: 'Develop the skills and capacity of cybersecurity professionals',
    },
    {
      icon: BuildingOfficeIcon,
      title: 'Industry Relationships',
      description: 'Build strong relationships across IT and cybersecurity industries',
    },
    {
      icon: DocumentTextIcon,
      title: 'Policy Advisory',
      description: 'Advise government on policy, law, and legislation',
    },
    {
      icon: SparklesIcon,
      title: 'Innovation Forums',
      description: 'Provide forums to share knowledge, skills, and innovation',
    },
    {
      icon: ShieldCheckIcon,
      title: 'Member Services',
      description: 'Support members with professional services and resources',
    },
    {
      icon: UserGroupIcon,
      title: 'Youth Programs',
      description: 'Create youth programs and forums to nurture the next generation',
    },
    {
      icon: BuildingOfficeIcon,
      title: 'Public-Private Partnerships',
      description: 'Foster partnerships to advance national cybersecurity initiatives',
    },
    {
      icon: GlobeAltIcon,
      title: 'Multi-Stakeholder Synergy',
      description: 'Establish synergy among industry, government, and academia',
    },
  ];

  const keyContributions = [
    {
      icon: TrophyIcon,
      title: 'Research Excellence',
      description: 'Awarded Cybersecurity Research Excellence at Nigeria\'s inaugural National Cybersecurity Conference (2025)',
    },
    {
      icon: PresentationChartLineIcon,
      title: 'Pioneering Conferences',
      description: 'Pioneered the Cyber Secure Nigeria Conference (2015) and hosted over a dozen national conferences',
    },
    {
      icon: GlobeAltIcon,
      title: 'Global Leadership',
      description: 'Partnered with Stop. Think. Connect. Initiative and presented at Bern Symposium on Global Cybersecurity',
    },
    {
      icon: TrophyIcon,
      title: 'Merit Awards',
      description: 'Established Nigeria\'s first national awards recognizing excellence across individuals, institutions, and organizations',
    },
    {
      icon: UserGroupIcon,
      title: 'Youth Empowerment',
      description: 'Delivered cybersecurity programs to secondary students and hosted annual hackathons nationwide',
    },
    {
      icon: DocumentTextIcon,
      title: 'Policy Engagement',
      description: 'Contributed to National Cybersecurity Policy (2021), Cybercrime Prohibition Act (2025), and national committees',
    },
    {
      icon: AcademicCapIcon,
      title: 'Academic Presence',
      description: 'Established CyberClubs in universities and polytechnics, mentoring students nationwide',
    },
    {
      icon: GlobeAltIcon,
      title: 'National Spread',
      description: 'Active chapters across all geopolitical zones, engaging professionals and students nationwide',
    },
  ];

  const partners = [
    { name: 'National Security Adviser', category: 'Government' },
    { name: 'NITDA', category: 'Government' },
    { name: 'Senate Committee on ICT & Cybersecurity', category: 'Government' },
    { name: 'FBI Nigeria Office', category: 'International' },
    { name: 'Commonwealth Security & Resilience Group', category: 'International' },
    { name: 'Israeli Ministry of Trade & Economy', category: 'International' },
    { name: 'AWS', category: 'Technology' },
    { name: 'Dubai World Trade Centre', category: 'International' },
    { name: 'GITEX Global', category: 'International' },
  ];

  return (
    <div className="min-h-screen bg-white">
      {/* Hero Section */}
      <div className="relative min-h-[600px] flex items-center justify-center overflow-hidden">
        {/* Background Image */}
        <div
          className="absolute inset-0 bg-cover bg-center bg-no-repeat"
          style={{
            backgroundImage: 'url(/cseangroup.jpeg)',
          }}
        >
          <div className="absolute inset-0 bg-gray-900/70"></div>
        </div>

        {/* Hero Content */}
        <div className="relative z-10 container-max px-4 sm:px-6 lg:px-8 py-20">
          <div className="max-w-4xl mx-auto text-center">
            <h1 className="text-4xl md:text-6xl font-bold text-white mb-6 font-display">
              {ENV_CONFIG.siteName}
            </h1>
            <p className="text-xl md:text-2xl text-gray-200 mb-4 font-light">
              {ENV_CONFIG.siteTagline}
            </p>
            <p className="text-lg text-gray-300 mb-8 max-w-2xl mx-auto leading-relaxed">
              Nigeria's leading umbrella body of cybersecurity professionals. Together, we champion cybersecurity advocacy nationally, contributing to national cybersecurity policy and strategy development.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Link to="/membership/application" className="btn-primary inline-flex items-center justify-center">
                Join CSEAN
                <ArrowRightIcon className="w-5 h-5 ml-2" />
              </Link>
              <Link to="/about" className="btn-secondary inline-flex items-center justify-center">
                Learn More
              </Link>
            </div>
          </div>
        </div>
      </div>

      {/* Mission Statement Section */}
      <div className="bg-white section-padding">
        <div className="container-max">
          <div className="max-w-4xl mx-auto text-center">
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-6">
              Our Mission
            </h2>
            <div className="w-24 h-1 bg-primary-500 mx-auto mb-8"></div>
            <p className="text-xl text-gray-700 leading-relaxed mb-6">
              To build a global resourceful cybersecurity organisation leveraged on securing, positively transforming and enriching our digital experiences, socially and economically.
            </p>
            <p className="text-lg text-gray-600 leading-relaxed">
              Our mandate is simple yet nationally impactful: to strengthen Nigeria's resilience against cyber threats while building capacity for the future of a secure digital economy.
            </p>
          </div>
        </div>
      </div>

      {/* Statistics Section */}
      <div className="bg-gray-50 section-padding">
        <div className="container-max">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
            {statistics.map((stat, index) => (
              <div key={index} className="text-center">
                <div className="inline-flex items-center justify-center w-16 h-16 bg-primary-100 rounded-lg mb-4">
                  <stat.icon className="w-8 h-8 text-primary-600" />
                </div>
                <div className="text-4xl font-bold text-gray-900 mb-2">
                  {stat.number}
                </div>
                <div className="text-lg text-gray-600">
                  {stat.label}
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Objectives Section */}
      <div className="bg-white section-padding">
        <div className="container-max">
          <div className="text-center mb-12">
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
              Our Objectives
            </h2>
            <div className="w-24 h-1 bg-primary-500 mx-auto mb-4"></div>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              We champion cybersecurity awareness, best practices, and innovation through comprehensive initiatives
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {objectives.map((objective, index) => (
              <div
                key={index}
                className="card hover:shadow-xl transition-all duration-300"
              >
                <div className="flex items-start">
                  <div className="flex-shrink-0">
                    <div className="w-12 h-12 bg-primary-100 rounded-lg flex items-center justify-center">
                      <objective.icon className="w-6 h-6 text-primary-600" />
                    </div>
                  </div>
                  <div className="ml-4 flex-1">
                    <h3 className="text-lg font-semibold text-gray-900 mb-2">
                      {objective.title}
                    </h3>
                    <p className="text-gray-600 text-sm leading-relaxed">
                      {objective.description}
                    </p>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Key Contributions Section */}
      <div className="bg-gray-50 section-padding">
        <div className="container-max">
          <div className="text-center mb-12">
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
              Key National Contributions
            </h2>
            <div className="w-24 h-1 bg-primary-500 mx-auto mb-4"></div>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              Over the past decade, CSEAN has made landmark contributions to Nigeria's cybersecurity ecosystem
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            {keyContributions.map((contribution, index) => (
              <div
                key={index}
                className="card hover:shadow-xl transition-all duration-300"
              >
                <div className="flex flex-col items-center text-center">
                  <div className="w-14 h-14 bg-primary-100 rounded-lg flex items-center justify-center mb-4">
                    <contribution.icon className="w-7 h-7 text-primary-600" />
                  </div>
                  <h3 className="text-lg font-semibold text-gray-900 mb-3">
                    {contribution.title}
                  </h3>
                  <p className="text-gray-600 text-sm leading-relaxed">
                    {contribution.description}
                  </p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Partnerships Section */}
      <div className="bg-white section-padding">
        <div className="container-max">
          <div className="text-center mb-12">
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
              Engagement & Partnerships
            </h2>
            <div className="w-24 h-1 bg-primary-500 mx-auto mb-4"></div>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              CSEAN collaborates with national and international stakeholders to advance cybersecurity excellence
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-8">
            {['Government', 'International', 'Technology'].map((category) => (
              <div key={category} className="bg-gray-50 rounded-lg p-6">
                <h3 className="text-lg font-semibold text-gray-900 mb-4">
                  {category} Partners
                </h3>
                <ul className="space-y-2">
                  {partners
                    .filter((p) => p.category === category)
                    .map((partner, index) => (
                      <li key={index} className="text-gray-600 text-sm">
                        {partner.name}
                      </li>
                    ))}
                </ul>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Call to Action Section */}
      <div className="bg-primary-600 section-padding">
        <div className="container-max text-center">
          <h2 className="text-3xl md:text-4xl font-bold text-white mb-4">
            Join Nigeria's Leading Cybersecurity Community
          </h2>
          <p className="text-xl text-primary-100 mb-8 max-w-2xl mx-auto">
            Become part of a professional network that's shaping Nigeria's digital security landscape
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link
              to="/membership/application"
              className="bg-white text-primary-600 hover:bg-gray-50 font-semibold py-3 px-8 rounded-lg transition-colors duration-200 inline-flex items-center justify-center"
            >
              Start Your Application
              <ArrowRightIcon className="w-5 h-5 ml-2" />
            </Link>
            <Link
              to="/membership/benefits"
              className="border-2 border-white text-white hover:bg-white hover:text-primary-600 font-semibold py-3 px-8 rounded-lg transition-colors duration-200 inline-flex items-center justify-center"
            >
              View Benefits
            </Link>
          </div>
        </div>
      </div>
    </div>
  );
};

export default HomePage;

